import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { useState } from "react";
import type { Holiday } from "../types/holiday.types";
import { HolidayKind, HolidayKindLabel } from "../constants/holiday.constants";
import { createHoliday, updateHoliday } from "../services/HolidayService";
import schema from "./schema";
import { getHolidayYears } from "../utils/holidayYears";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIsoYear(ymd: string | undefined): number | null {
  if (!ymd || ymd.length < 4) return null;
  const y = Number(ymd.slice(0, 4));
  return Number.isFinite(y) ? y : null;
}

const YEARS = getHolidayYears();

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editing: Holiday | null;
  defaultDate?: string;
  onDelete?: () => void;
};

export function HolidayModal({ show, onClose, onSuccess, editing, defaultDate, onDelete }: Props) {
  const isEdit = editing != null;
  const currentYear = new Date().getFullYear();

  const editingYmd = editing?.date?.slice(0, 10) ?? "";
  const sourceYmd = editingYmd || defaultDate?.slice(0, 10) || "";
  const sourceYear = parseIsoYear(sourceYmd) ?? currentYear;
  const initialYear = YEARS.includes(sourceYear) ? sourceYear : currentYear;

  const [selectedYear, setSelectedYear] = useState<number>(initialYear);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name ?? "",
      date: editing?.date?.slice(0, 10) ?? defaultDate?.slice(0, 10) ?? "",
      kind: editing?.kind ?? HolidayKind.PUBLIC,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        date: values.date,
        kind: values.kind,
        applicableToAll: true,
        employmentCategoryIds: [],
      };

      try {
        if (isEdit && editing) {
          const res = await updateHoliday(editing.id, payload);
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          } else {
            toast.error(res.message ?? "Could not update holiday.");
          }
        } else {
          const res = await createHoliday(payload);
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          } else {
            toast.error(res.message ?? "Could not create holiday.");
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update holiday" : "Failed to create holiday"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const flatpickrBounds = {
    minDate: `${selectedYear}-01-01`,
    maxDate: `${selectedYear}-12-31`,
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    formik.setFieldValue("date", "");
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit holiday" : "Add holiday"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="holiday-modal-name">
              Name
            </Label>
            <Input
              id="holiday-modal-name"
              name="name"
              type="text"
              className="form-control"
              placeholder="e.g. Republic Day"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>

          <Row className="g-3 mb-3">
            <Col md={3}>
              <Label className="form-label" htmlFor="holiday-modal-year">
                Year
              </Label>
              <Input
                id="holiday-modal-year"
                type="select"
                className="form-select"
                value={selectedYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={5}>
              <Label className="form-label" htmlFor="holiday-modal-date">
                Date
              </Label>
              <Flatpickr
                key={`holiday-date-${selectedYear}-${editing?.id ?? "new"}`}
                id="holiday-modal-date"
                className={`form-control ${
                  formik.touched.date && formik.errors.date ? "is-invalid" : ""
                }`}
                value={formik.values.date || undefined}
                onChange={(dates: Date[]) => {
                  const d = dates[0];
                  if (d) formik.setFieldValue("date", toYmd(d));
                }}
                options={{
                  altInput: true,
                  altFormat: "d M, Y",
                  dateFormat: "Y-m-d",
                  ...flatpickrBounds,
                }}
                onBlur={() => formik.setFieldTouched("date", true)}
              />
              {formik.touched.date && formik.errors.date ? (
                <div className="invalid-feedback d-block">
                  {formik.errors.date}
                </div>
              ) : null}
            </Col>
            <Col md={4}>
              <Label className="form-label" htmlFor="holiday-modal-kind">
                Kind
              </Label>
              <Input
                id="holiday-modal-kind"
                name="kind"
                type="select"
                className="form-select"
                value={formik.values.kind}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.kind && formik.errors.kind)}
              >
                {(Object.values(HolidayKind) as HolidayKind[]).map((k) => (
                  <option key={k} value={k}>
                    {HolidayKindLabel[k]}
                  </option>
                ))}
              </Input>
              {formik.touched.kind && formik.errors.kind ? (
                <FormFeedback type="invalid">{formik.errors.kind}</FormFeedback>
              ) : null}
            </Col>
          </Row>

          <div className="d-flex justify-content-between gap-2 pt-2">
            <div>
              {isEdit && onDelete ? (
                <Button type="button" color="danger" outline onClick={onDelete}>
                  <i className="ri-delete-bin-line align-bottom me-1" />
                  Delete
                </Button>
              ) : null}
            </div>
            <div className="d-flex gap-2">
              <Button type="button" color="light" onClick={handleClose}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={formik.isSubmitting}>
                {isEdit ? "Save changes" : "Create holiday"}
              </Button>
            </div>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
